import { Injectable, Signal, computed, inject } from '@angular/core';
import { REQUEST_STATE } from '@lentau/ui-kit/core/enums/request-state.enum';
import { UserStore } from './user.store';
import { UserProfile } from 'angular-api-gateway/v1/model/userProfile';
import { UpdateUserProfileRequest } from 'angular-api-gateway/v1/model/updateUserProfileRequest';
import { Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { UserProfileConsent } from 'angular-api-gateway/v1/model/userProfileConsent';
import { UserProfileUpdateOptions } from './user.types';
import { LuToastMessage } from '@lentau/ui-kit/molecules/toast/shared/lu-toast-message.interface';

@Injectable({ providedIn: 'root' })
export class UserFacade {
  private store: InstanceType<typeof UserStore> = inject(UserStore);

  public user: Signal<UserProfile> = this.store.user;
  public user$: Observable<UserProfile> = toObservable(this.user);

  public prevUser: Signal<UserProfile> = this.store.prevUser;
  public prevUser$: Observable<UserProfile> = toObservable(this.prevUser);

  public requestState: Signal<REQUEST_STATE> = this.store.requestState;
  public requestState$: Observable<REQUEST_STATE> = toObservable(this.requestState);
  public isLoaded: Signal<boolean> = this.store.isLoaded;
  public isLoaded$: Observable<boolean> = toObservable(this.isLoaded);
  public isFail: Signal<boolean> = computed(() => this.store.requestState() === REQUEST_STATE.FAIL);
  public isFail$: Observable<boolean> = toObservable(this.isFail);
  public isLoading: Signal<boolean> = computed(() => this.store.requestState() === REQUEST_STATE.LOADING);
  public isLoading$: Observable<boolean> = toObservable(this.isLoading);
  public isAvatarUploading: Signal<boolean> = computed(
    () => this.store.avatarUploadRequestState() === REQUEST_STATE.LOADING
  );

  public cellularPhone: Signal<string> = this.store.cellularPhone;

  public segments: Signal<string[]> = this.store.segments;
  public segments$: Observable<string[]> = toObservable(this.segments);

  public buyerId: Signal<number> = this.store.buyerId;
  public buyerId$: Observable<number> = toObservable(this.buyerId);

  public crmClientNumber: Signal<string> = this.store.crmClientNumber;
  public crmClientNumber$: Observable<string> = toObservable(this.crmClientNumber);

  public passportId: Signal<string> = this.store.passportId;
  public passportId$: Observable<string> = toObservable(this.passportId);

  public legalEntityId: Signal<string> = this.store.legalEntityId;
  public legalEntityId$: Observable<string> = toObservable(this.legalEntityId);

  public isNewcomer: Signal<boolean> = this.store.isNewcomer;

  public consent: Signal<UserProfileConsent> = this.store.consent;

  public isCacheInit: Signal<boolean> = this.store.isCacheInit;
  public isCacheInit$: Observable<boolean> = toObservable(this.isCacheInit);

  public avatar: Signal<string> = this.store.avatar;
  public hasAvatar: Signal<boolean> = computed(() => !!this.store.avatar());

  public userProfileChanged: Signal<UserProfile | null> = this.store.userProfileChanged;

  public updateUserProfile(
    profile: UpdateUserProfileRequest,
    message?: LuToastMessage,
    options?: UserProfileUpdateOptions
  ): void {
    this.store.updateUserProfile(profile, message, options);
  }

  public getUserProfile(): void {
    this.store.getUserProfile();
  }

  // Не дёргать отдельно от других связанных сторов
  public reset(): void {
    this.store.reset();
  }

  public setProfileCache(userProfile: Partial<UserProfile>): void {
    this.store.setProfileCache(userProfile);
  }

  public patchUsersAvatar(file: File): void {
    this.store.patchUsersAvatar(file);
  }

  public deleteUsersAvatar(): void {
    this.store.deleteUsersAvatar();
  }

  public updateLegalEntityId(legalEntityId: string): void {
    this.store.updateLegalEntityId(legalEntityId);
  }
}
